import { ReactNode } from 'react';

import { useUnit } from 'effector-react';

import { KeycloakService } from '@m/keycloak-service/KeycloakService';
import {
  useActiveMandate,
  useAcceptMandate,
  useDefaultPaymentMethod,
} from 'apps/portal/hooks/useMandate';
import { useSelectedEnterpriseId } from 'apps/portal/hooks/useSelectedEnterpriseId';
import { authStore } from 'apps/portal/state/auth';

import { ReviewPaymentPermissionsModal } from './ReviewPaymentPermissionsModal';

const BANK_ACCOUNT_PREFIX = 'ba_';

type MandateGuardProps = {
  children: ReactNode;
};

/**
 * Blocks the billing portal with a non-dismissable "Review payment permissions" modal when
 * the enterprise's default payment method is a bank account (`ba_...`) but has no active ACH
 * mandate.
 *
 * The portal serves two experiences in one app, split by user type rather than route:
 * Keycloak (`PORTAL_ASSOCIATE`) users get the parking-pass experience, everyone else gets
 * billing. We only block authenticated billing users — never parking-pass users, and never
 * the unauthenticated login / welcome screens.
 *
 * Rendered once, wrapping the portal route switch.
 */
export function MandateGuard({ children }: MandateGuardProps) {
  const { authenticated } = useUnit(authStore);
  const enterpriseId = useSelectedEnterpriseId();

  // The portal serves billing and parking-pass experiences split by user type. Any Keycloak
  // session (parking-pass / PORTAL_ASSOCIATE) reports `isLoggedIn()`, so a billing user is an
  // authenticated session that is NOT a Keycloak session. Never true on login / welcome.
  const isBillingUser = authenticated === true && !KeycloakService.isLoggedIn();

  const { data: defaultPaymentMethodData } = useDefaultPaymentMethod(
    isBillingUser ? enterpriseId : null,
  );
  const paymentMethod = defaultPaymentMethodData?.paymentMethod ?? null;
  const isBankAccount = Boolean(
    paymentMethod?.paymentProviderMethodId?.startsWith(BANK_ACCOUNT_PREFIX),
  );
  const userPaymentMethodId = paymentMethod?.id ?? null;

  const mandateCheckEnabled = isBillingUser && isBankAccount;
  const { data: activeMandate, isPending: mandatePending } = useActiveMandate(
    enterpriseId,
    userPaymentMethodId,
    mandateCheckEnabled,
  );

  // Fail closed: block whenever we don't have a *confirmed* active mandate. This is a
  // compliance gate (ACH authorization), so a backend failure must not let an unauthorized
  // bank-account user through. `activeMandate` is `undefined` on error (after 3 retries) and
  // `null` on a successful "no mandate" response — we block in both cases via `!activeMandate`.
  // `!mandatePending` prevents flashing the modal while the check and its retries are in flight.
  // The accept-mandate POST is a separate endpoint, so the user can still authorize even if
  // the GET errored.
  const shouldBlock =
    mandateCheckEnabled && !mandatePending && !activeMandate && userPaymentMethodId != null;

  // Powers the modal's "Authorize & continue" CTA (passed as `onAuthorize` below): records the
  // mandate, then invalidates the active-mandate query so the re-check dismisses the block.
  const {
    mutate: acceptMandate,
    isPending: accepting,
    isError: acceptError,
  } = useAcceptMandate(enterpriseId);

  return (
    <>
      {children}
      {shouldBlock && (
        <ReviewPaymentPermissionsModal
          open
          loading={accepting}
          error={acceptError}
          onAuthorize={() => acceptMandate(userPaymentMethodId)}
        />
      )}
    </>
  );
}
